/**
 * The four origins (docs/design/C0_OPENING_PLAINS.md §1, C0_TOWNS_BCD.md): where a new character's story starts.
 * B-D start in greybox towns another lane builds (data/layouts/townB..D); they are named here by zone id only and
 * stay `ready: false` until the town is playable. The pick is stored on the progress as a `origin-<id>` scene mark
 * (no world exists yet at character creation, so it is a pure function, not a PlayerCommand).
 */
import type { PlayerProgress } from '../progress';
import type { ZoneId } from './zones';

export type OriginId = 'a' | 'b' | 'c' | 'd';

export interface OriginDef {
  id: OriginId;
  name: string;
  /** Starting town (Thai). */
  town: string;
  pitch: string;
  /** Zone id the story starts in. */
  zone: string;
  /** Playable now? B-D flip to true when their town is in. */
  ready: boolean;
}

export const ORIGINS: readonly OriginDef[] = [
  { id: 'a', name: 'จดหมายของพี่', town: 'ลานศิลาเก้ากระบี่', pitch: 'ตามจดหมายของพี่ชายมาถึงศิลา แล้วตื่นขึ้นพร้อมรอยบนฝ่ามือ', zone: 'plains', ready: true },
  { id: 'b', name: 'หนีจากเหมืองเงาผา', town: 'เหมืองเงาผา', pitch: 'ตื่นในหลุมเหมืองทั้งโซ่ตรวน ท่ามกลางผู้มีรอยเหมือนกัน', zone: 'town-b', ready: true },
  { id: 'c', name: 'ระฆังที่ไม่ยอมดัง', town: 'ท่าระฆังเงียบ', pitch: 'คืนที่ระฆังใหญ่ไม่ดัง และหลุมศพริมน้ำเปิดออก', zone: 'town-c', ready: true },
  { id: 'd', name: 'เวทีงิ้วโคมเก้าสาย', town: 'เมืองโคมเก้าสาย', pitch: 'ได้รอยกลางเวทีต่อหน้าคนทั้งเมือง แล้วนักสู้ก็ต่อแถวท้า', zone: 'town-d', ready: true },
];

export const originById = (id: string): OriginDef | undefined => ORIGINS.find((o) => o.id === id);
export const originMark = (id: OriginId): string => `origin-${id}`;

/** The origin chosen on a progress; 'a' when none is marked (every older save started on the plains). */
export function originOf(p: PlayerProgress): OriginId {
  for (const o of ORIGINS) if (p.scenes.includes(originMark(o.id))) return o.id;
  return 'a';
}

/** Stores the pick: refuses an unknown or not-ready origin. 'a' changes nothing but the mark. B-D move the start. */
export function chooseOrigin(p: PlayerProgress, id: string): boolean {
  const o = originById(id);
  if (!o || !o.ready) return false;
  p.scenes = p.scenes.filter((s) => !ORIGINS.some((x) => originMark(x.id) === s));
  p.scenes.push(originMark(o.id));
  if (o.id !== 'a') {
    p.location = o.zone as ZoneId; // B-D ids join ZoneId when their zones register
    p.arriveFrom = null;
    p.indoors = null;
  }
  return true;
}
